import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { gruposItensApi } from '../../api/endpoints'
import { Badge } from '../../components/ui/Badge'
import { Button } from '../../components/ui/Button'
import { Card, CardHeader, KpiCard } from '../../components/ui/Card'
import { ErrorState, LoadingState } from '../../components/ui/EmptyState'
import { Table, THead, Th, Tr, Td } from '../../components/ui/Table'
import { PageHeader } from '../../layout/PageHeader'
import { formatDate, formatMoney } from '../../lib/utils'
import { BloqueioPremium } from './BloqueioPremium'
import { GrupoModal } from './GrupoModal'
import { ROTULO_ESSENCIAL, VARIANTE_ESSENCIAL, resumoRegras } from './rotulosItens'
import { usePermiteNotas } from './usePermiteNotas'

const UNIDADE = { KG: 'kg', L: 'L', UN: 'un' } as const

/** Detalhe de um grupo: preço por marca (mediana do preço padrão), compras e regras. */
export function GrupoDetalhePage() {
  const { id = '' } = useParams()
  const { permite } = usePermiteNotas()
  const [editando, setEditando] = useState(false)
  const { data: d, isLoading, isError, error } = useQuery({ queryKey: ['itens-grupo-detalhe', id], queryFn: () => gruposItensApi.detalhe(id), enabled: permite && !!id })

  const maisBarata = d?.porMarca.length ? d.porMarca.reduce((a, b) => (b.medianaPrecoPadrao < a.medianaPrecoPadrao ? b : a)).marca : null

  return (
    <>
      <PageHeader title={d ? d.grupo.nome : 'Grupo de itens'} />
      <div className="mb-3"><Link to="/grupos-itens" className="text-13 text-muted hover:text-white"><i className="ri-arrow-left-line" aria-hidden /> Todos os grupos</Link></div>
      {!permite ? <BloqueioPremium recurso="Os grupos de itens" /> : isLoading ? <LoadingState /> : isError || !d ? <ErrorState message={(error as Error | null)?.message ?? 'Grupo não encontrado.'} /> : (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-3">
            <KpiCard title="Gasto no grupo" value={formatMoney(d.totalGasto)} icon="ri-wallet-3-line" tone="expense" />
            <KpiCard title="Compras" value={String(d.grupo.totalCompras)} icon="ri-shopping-cart-2-line" tone="info" />
            <KpiCard title="Padrão" value={ROTULO_ESSENCIAL[d.grupo.essencial]} icon="ri-scales-3-line" tone={d.grupo.essencial === 1 ? 'income' : d.grupo.essencial === 2 ? 'warning' : 'muted'} />
          </div>

          <Card>
            <CardHeader title="Preço por marca" icon="ri-price-tag-3-line" />
            <div className="p-4">
              <p className="mb-3 text-2xs text-muted">Mediana das compras no preço por kg, litro ou unidade, para comparar embalagens diferentes.</p>
              {d.porMarca.length === 0 ? <p className="text-13 text-muted">Ainda não há compras com marca neste grupo.</p> : (
                <div className="space-y-2">
                  {d.porMarca.map((m) => (
                    <div key={m.marca} className="flex flex-wrap items-center justify-between gap-2 rounded bg-surface2 px-3 py-2">
                      <span className="font-medium text-white">{m.marca} <span className="text-2xs font-normal text-muted">· {m.compras} {m.compras === 1 ? 'compra' : 'compras'}</span></span>
                      <span className="text-13 text-white">
                        {formatMoney(m.medianaPrecoPadrao)}/{UNIDADE[m.unidade]}
                        {m.marca === maisBarata && d.porMarca.length > 1 && <Badge variant="income" className="ml-2">mais barato</Badge>}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>

          <Card>
            <CardHeader title="Compras do grupo" icon="ri-list-check-2" />
            <Table>
              <THead><tr><Th>Item</Th><Th>Data</Th><Th>Loja</Th><Th className="text-right">Qtd</Th><Th className="text-right">Valor</Th><Th className="text-right">Preço padrão</Th></tr></THead>
              <tbody>
                {d.compras.map((c, i) => (
                  <Tr key={`${c.notaId}-${i}`}>
                    <Td>
                      <p className="text-white">{c.marca ?? c.descricao}</p>
                      <p className="text-2xs text-muted">{c.pesoBase.valor} {UNIDADE[c.pesoBase.unidade]} · {c.descricao}</p>
                    </Td>
                    <Td className="whitespace-nowrap text-muted">{formatDate(c.data)}</Td>
                    <Td className="text-muted">{c.estabelecimento}</Td>
                    <Td className="text-right text-muted">{c.quantidade}</Td>
                    <Td className="whitespace-nowrap text-right text-white">{formatMoney(c.valorUnitario)}</Td>
                    <Td className="whitespace-nowrap text-right font-medium text-white">{formatMoney(c.precoPadrao)}/{UNIDADE[c.pesoBase.unidade]}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </Card>

          <Card>
            <CardHeader title="Regras deste grupo" icon="ri-filter-3-line" actions={<Button variant="light" icon="ri-pencil-line" onClick={() => setEditando(true)}>Editar regras</Button>} />
            <div className="space-y-2 p-4">
              <Badge variant={VARIANTE_ESSENCIAL[d.grupo.essencial]}>{ROTULO_ESSENCIAL[d.grupo.essencial]}</Badge>
              <p className="text-13 text-muted">{resumoRegras(d.grupo.regras)}</p>
            </div>
          </Card>
        </div>
      )}
      {editando && d && <GrupoModal grupo={d.grupo} onClose={() => setEditando(false)} />}
    </>
  )
}
